/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { TrialData } from '../../../ts/plan';
import { AsyncAppThunk } from '../../../ts/state';
import type * as instanceActions from '../store/instance/actions';
import { plusTrialLength } from './config';
import localStorage from './localStorage';

const DAYS_TO_MILLIS = 24 * 60 * 60 * 1000;
const TRIAL_REMINDERS = 'plusTrialReminder';

export type TrialActions = typeof instanceActions;

class PlusTrialManager {
  instance: string;
  supported: boolean;
  trialData: TrialData;
  trialActions?: TrialActions;
  // Note: trialActions param is not required unless starting or extending a trial.
  constructor(
    instance: string,
    trialData: TrialData,
    trialActions?: TrialActions
  ) {
    this.instance = instance;
    this.supported = !!plusTrialLength && !!trialData;
    this.trialData = trialData;
    this.trialActions = trialActions;
  }

  // Helper functions
  _addDays(start: Date, days: number): string {
    const end = start.getTime() + days * DAYS_TO_MILLIS;
    return new Date(end).toJSON();
  }

  /*
   * Returns true if trial timer support is provided by the underlying store
   */
  trialSupported() {
    return this.supported;
  }

  /*
   * Returns true is a plus trial has been started
   */
  trialStarted() {
    return Boolean(this.supported && this.trialData.start_date);
  }

  /*
   * Return trial period length in days
   */
  trialLength(): number {
    return plusTrialLength;
  }

  /*
   * Sets a new plus trial start date.
   * Returns a promise that resolves when the trial is started.
   */
  startTrial() {
    if (this.trialActions?.updatePlusTrialData) {
      const now = new Date();
      return this.trialActions.updatePlusTrialData({
        start_date: this._addDays(now, 0),
        end_date: this._addDays(now, plusTrialLength),
        extensions: 0
      });
    }
    return Promise.reject(
      new Error('updatePlusTrialData missing from TrialManager actions')
    );
  }

  /*
   * Extends the current trial
   * Returns a promise that resolves when the trial is extended.
   */
  extendTrial(extensions: number): AsyncAppThunk | Promise<never> {
    if (this.trialData && this.trialActions?.updatePlusTrialData) {
      const now = new Date();
      this.resetReminder();
      return this.trialActions.updatePlusTrialData({
        start_date: this.trialData.start_date,
        end_date: this._addDays(now, extensions * plusTrialLength),
        extensions: this.trialData.extensions + extensions
      });
    }
    return Promise.reject(
      new Error('updatePlusTrialData missing from TrialManager actions')
    );
  }

  /*
   * Returns the number of days remaining in the trial.
   *
   * Note: will return undefined (meaning trial always valid) if no trial
   * timer support.
   * Note 2: Take care that a return of 0 is _not_ the same as 'undefined'!
   */
  daysRemaining() {
    let result = undefined;
    if (this.supported) {
      if (this.trialData.end_date) {
        const now = new Date().getTime();
        const end = new Date(this.trialData.end_date).getTime();
        result = Math.max(Math.ceil((end - now) / DAYS_TO_MILLIS), 0);
      } else {
        result = plusTrialLength;
      }
    }
    return result;
  }

  /*
   * Returns the number of extensions set for the current trial.
   *
   * Note: will return undefined if no trial timer support.
   * Note 2: Take care that a return of 0 is _not_ the same as 'undefined'!
   */
  trialExtensions() {
    let result = undefined;
    if (this.supported) {
      if (this.trialData.extensions) {
        result = this.trialData.extensions;
      } else {
        result = 0;
      }
    }
    return result;
  }

  /*
   * Returns true if an upgrade reminder pop-up is needed.
   */
  reminderNeeded(daysRemaining: number) {
    let result = false;
    if (this.supported && daysRemaining > 0) {
      // Try to find a reminder threshold in localStorage.  If none, default to
      // 2/3 of trial length.
      const reminders = localStorage.get(TRIAL_REMINDERS) || {};
      const next = reminders[this.instance];
      const threshold = next || next === 0 ? next : (plusTrialLength * 2) / 3;
      result = daysRemaining <= threshold;
    }
    return result;
  }

  /*
   * Record a new threshold to delay the next reminder pop-up.
   */
  delayReminder(daysRemaining: number) {
    if (this.supported) {
      let threshold = 0;
      if (daysRemaining > 1 && daysRemaining <= 5) {
        threshold = 1;
      } else if (daysRemaining > 5 && daysRemaining <= 10) {
        threshold = 5;
      } else if (daysRemaining > 10) {
        const increment = plusTrialLength / 3;
        threshold = Math.floor((daysRemaining - 1) / increment) * increment;
      }
      const reminders = localStorage.get(TRIAL_REMINDERS) || {};
      reminders[this.instance] = threshold;
      localStorage.set(TRIAL_REMINDERS, reminders);
    }
  }

  /*
   * Reset reminder threshold to defaults
   */
  resetReminder() {
    if (this.supported) {
      const reminders = localStorage.get(TRIAL_REMINDERS) || {};
      delete reminders[this.instance];
      localStorage.set(TRIAL_REMINDERS, reminders);
    }
  }
}

export default PlusTrialManager;
